import React, { FC, useState, useEffect } from 'react';
import { Card, Typography, Divider, Tag, Collapse, Alert } from 'antd';
import { DatabaseOutlined, PartitionOutlined } from '@ant-design/icons';
import { useNewIntegrationsStore } from '@/store/newIntegration';
import { useEdgesStore } from '@/store/edges';
import ReactJson from 'react-json-view';

const { Text, Title } = Typography;
const { Panel } = Collapse;

interface ListOutputPreviewProps {
  sourceData: any;
  embedded?: boolean;
}

const ListOutputPreview: FC<ListOutputPreviewProps> = ({ sourceData, embedded }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const newIntegration = useNewIntegrationsStore(state => state.newIntegration);
  const edges = useEdgesStore(state => state.edges);

  useEffect(() => {
    // Only generate preview if list mode is enabled
    if (!newIntegration?.source?.isList) {
      setPreviewData(null);
      return;
    }

    // Generate a preview of how the data will be processed
    const generatePreview = () => {
      try {
        const listConfig = newIntegration?.source?.listConfig || { 
          type: 'simple',
          iterationPath: newIntegration?.source?.listPath || '',
          outputConfig: { strategy: 'individual' } 
        };
        
        // Get sample list data
        let sampleListData: any[] = [];
        
        if (Array.isArray(sourceData)) {
          sampleListData = sourceData.slice(0, 2); // Take first two items
        } else if (typeof sourceData === 'object' && sourceData !== null) {
          // Try to extract from a nested path
          const path = listConfig.iterationPath || newIntegration?.source?.listPath || '';
          if (path) {
            const pathParts = path.split('.');
            let data = sourceData;
            for (const part of pathParts) {
              if (data && typeof data === 'object' && part in data) {
                data = data[part];
              } else {
                data = null;
                break;
              }
            }
            
            if (Array.isArray(data)) {
              sampleListData = data.slice(0, 2);
            }
          }
        }
        
        // If we still don't have sample data, create some
        if (sampleListData.length === 0) {
          // Create sample data based on the edges
          const sampleItem: Record<string, string> = {};
          edges.forEach(edge => {
            if (edge.sourcePath) {
              sampleItem[edge.sourcePath] = `Sample ${edge.sourcePath} value`;
            }
          });
          
          sampleListData = [sampleItem, { ...sampleItem, id: '2' }];
        }
        
        // Generate preview based on the output strategy
        const strategy = listConfig.outputConfig?.strategy || 'individual';
        let previewResult: any;
        
        switch (strategy) {
          case 'individual':
            previewResult = {
              description: 'Each item in the list will be processed individually',
              example: sampleListData.map(item => {
                const mappedItem: Record<string, any> = {};
                edges.forEach(edge => {
                  if (edge.targetPath && edge.sourcePath && item[edge.sourcePath]) {
                    mappedItem[edge.targetPath] = item[edge.sourcePath];
                  } else if (edge.targetPath) {
                    mappedItem[edge.targetPath] = `Mapped from ${edge.sourcePath || 'source'}`;
                  }
                });
                return mappedItem;
              }),
            };
            break;
            
          case 'bulk':
            previewResult = {
              description: 'All items will be sent in a single bulk array',
              example: {
                items: sampleListData.map(item => {
                  const mappedItem: Record<string, any> = {};
                  edges.forEach(edge => {
                    if (edge.targetPath && edge.sourcePath && item[edge.sourcePath]) {
                      mappedItem[edge.targetPath] = item[edge.sourcePath];
                    } else if (edge.targetPath) {
                      mappedItem[edge.targetPath] = `Mapped from ${edge.sourcePath || 'source'}`;
                    }
                  });
                  return mappedItem;
                }),
              },
            };
            break;
            
          case 'aggregate':
            const aggregateField = listConfig.outputConfig?.aggregateField || 'items';
            const aggregatedResult: Record<string, any> = {
              [aggregateField]: [],
            };
            
            sampleListData.forEach(item => {
              const mappedItem: Record<string, any> = {};
              edges.forEach(edge => {
                if (edge.targetPath && edge.sourcePath && item[edge.sourcePath]) {
                  mappedItem[edge.targetPath] = item[edge.sourcePath];
                } else if (edge.targetPath) {
                  mappedItem[edge.targetPath] = `Mapped from ${edge.sourcePath || 'source'}`;
                }
              });
              aggregatedResult[aggregateField].push(mappedItem);
            });
            
            previewResult = {
              description: `Items will be aggregated into a single object field: "${aggregateField}"`,
              example: aggregatedResult,
            };
            break;
        }
        
        setPreviewData(previewResult);
      } catch (e) {
        console.error('Failed to generate list output preview', e);
        setPreviewData({
          description: 'Could not generate preview due to an error',
          example: {},
        });
      }
    };
    
    generatePreview();
  }, [sourceData, newIntegration, edges]);

  // If list mode is not enabled or we couldn't generate a preview, don't render
  if (!newIntegration?.source?.isList || !previewData) {
    return null;
  }

  return (
    <Card 
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <PartitionOutlined style={{ marginRight: 8, color: '#1664ff' }} />
          <Text strong>List Processing Output Preview</Text>
          <Tag color="blue" style={{ marginLeft: 12 }}>
            {newIntegration.source.listConfig?.type || 'simple'}
          </Tag>
          <Tag color="purple" style={{ marginLeft: 4 }}>
            {newIntegration.source.listConfig?.outputConfig?.strategy || 'individual'}
          </Tag>
        </div>
      }
      size="small"
      style={{ 
        marginTop: embedded ? 0 : 16,
        marginBottom: embedded ? 0 : 24,
        border: '1px solid #d6e8ff',
        borderRadius: 8,
        boxShadow: embedded ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.05)'
      }}
    >
      <Alert 
        message={previewData.description}
        type="info" 
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <Collapse 
        defaultActiveKey={[]}
        onChange={(keys) => setIsExpanded(keys.length > 0)}
        ghost
      >
        <Panel 
          header={
            <Text>
              <DatabaseOutlined style={{ marginRight: 8 }} />
              {isExpanded ? 'Hide' : 'Show'} output format preview
            </Text>
          } 
          key="1"
        >
          <div style={{ maxHeight: 300, overflow: 'auto', backgroundColor: '#f9f9f9', padding: 8, borderRadius: 4 }}>
            <ReactJson 
              src={previewData.example} 
              name={null}
              collapsed={1}
              displayDataTypes={false}
              enableClipboard={false}
              style={{ backgroundColor: '#f9f9f9' }}
            />
          </div>
        </Panel>
      </Collapse>
    </Card>
  );
};

export default ListOutputPreview; 